<template lang="pug">
  .status-dialog(v-loading="loadingStatistics")
    .status-header.mb-4
      .status-card
        .status-card-column
          .status-card-row
            i.el-icon-time.status-icon
            span.status-label {{ $t('transmitters.status.last_transmission') }}
            span.status-value {{ transmitter.last_transmission ? buildDateTime(transmitter.last_transmission) : '-' }}
    el-radio-group.mb-3(
      v-model="category"
      fill="#2e7d32"
      text-color="#ffffff"
      @change="handleCategoryChange"
    )
      el-radio-button(label="general_info" :title="$t('transmitters.status.general_info_title')") {{ $t('transmitters.status.general_info') }}
      el-radio-button(label="heartbeat" :title="$t('transmitters.status.heartbeat_title')") {{ $t('transmitters.status.heartbeat') }}
      el-radio-button(label="full_tx" :title="$t('transmitters.status.full_tx_title')") {{ $t('transmitters.status.full_tx') }}
      el-radio-button(label="combined" :title="$t('transmitters.status.combined_title')") {{ $t('transmitters.status.combined') }}

    .filters
      FilterDatePicker(
        v-model="dateRange"
        :resetSignal="resetKey"
        @onChange="handleChange"
      )
      el-button.el-icon-refresh-left.mr-2.cursor-pointer(
        style="font-size: 35px; margin-right: 0px;"
        @click="reloadData()"
      )
    br
    PowerChart(
      :chartData="transmitterInfos"
      :pairs="activePairs"
      @onZoom="handleZoom"
    )
</template>

<script>
import FilterDatePicker from "@components/_shared/FilterDatePicker";
import PowerChart from "@components/charts/PowerChart.vue";
import { mapActions, mapMutations, mapState } from "vuex";

const GENERAL_INFO_PAIRS = [
  { key: "ext_voltage", label: "Ext Voltage" },
  { key: "bat_voltage", label: "Bat Voltage" },
];

const HEARTBEAT_PAIRS = [
  { key: "ext_pre_sleep", label: "Ext Pre-Sleep" },
  { key: "bat_pre_sleep", label: "Bat Pre-Sleep" },
  { key: "ext_pre_lora", label: "Ext Pre-Lora" },
  { key: "bat_pre_lora", label: "Bat Pre-Lora" },
];

const FULL_TX_PAIRS = [
  ...HEARTBEAT_PAIRS,
  { key: "ext_sensor_start", label: "Ext Sensor Start" },
  { key: "bat_sensor_start", label: "Bat Sensor Start" },
  { key: "ext_sensor_end", label: "Ext Sensor End" },
  { key: "bat_sensor_end", label: "Bat Sensor End" },
];

export default {
  name: "ViewStatus",

  components: { PowerChart, FilterDatePicker },

  data() {
    return {
      transmitterInfos: [],
      dateRange: [],
      category: "general_info",
      loadingStatistics: false,
      resetKey: null,
    };
  },

  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("transmitter", ["transmitter"]),

    activePairs() {
      if (this.category === "full_tx" || this.category === "combined") return FULL_TX_PAIRS;
      if (this.category === "heartbeat") return HEARTBEAT_PAIRS;
      return GENERAL_INFO_PAIRS;
    },

    firmwareOutdated() {
      return (
        this.transmitter.firmware_version &&
        this.transmitter.site_latest_firmware_build_time &&
        this.transmitter.firmware_version !== this.transmitter.site_latest_firmware_build_time
      );
    },
  },

  async created() {
    await this.handleChange(this.dateRange);
  },

  methods: {
    ...mapActions("transmitterInfo", ["getSensorTransmitterInfo"]),

    ...mapMutations("transmitterInfo", ["clearTransmitterInfoCache"]),


    handleCategoryChange() {
      this.handleChange(this.dateRange);
    },

    reloadData() {
      this.clearTransmitterInfoCache();
      this.resetKey = Date.now();
      this.handleChange(this.dateRange);
    },

    buildDateTime(dateValue) {
      const localDate = new Date(dateValue);
      const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      const day = localDate.getDate().toString().padStart(2, "0");
      const monthName = months[localDate.getMonth()];
      const year = localDate.getFullYear();
      const timeStr = localDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
      return `${timeStr} on ${day}-${monthName}-${year}`;
    },

    async handleZoom([start, end]) {
      this.dateRange = [start, end];
      await this.handleChange(this.dateRange);
    },

    async handleChange(values) {
      this.transmitterInfos = [];

      if (!values || values.length === 0) {
        return;
      }

      this.loadingStatistics = true;

      const dateParams = `&by_date_range[start_date]=${values[0]}&by_date_range[end_date]=${values[1]}`;
      const base = `?by_sensor_transmitter_id=${this.transmitter.id}`;

      this.dateRange = values;

      if (this.category === "combined") {
        const [heartbeat, fullTx] = await Promise.all([
          this.getSensorTransmitterInfo(`${base}&by_category=heartbeat${dateParams}`),
          this.getSensorTransmitterInfo(`${base}&by_category=full_tx${dateParams}`),
        ]);

        this.transmitterInfos = [...heartbeat, ...fullTx].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
      } else {
        this.transmitterInfos = await this.getSensorTransmitterInfo(
          `${base}&by_category=${this.category}${dateParams}`
        );
      }

      this.loadingStatistics = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.status-header {
  .status-card {
    background: #f7f9fa;
    border-radius: 8px;
    padding: 14px 18px;
    border: 1px solid #e4e7ec;
    display: flex;              // NEW: lay the two columns side by side
    gap: 24px;

    .status-card-column {
      flex: 1;                  // NEW: each column takes equal width
      display: flex;            // CHANGED: was `display: grid` with 2 cols
      flex-direction: column;   // CHANGED: stack rows vertically
    
      .status-card-row {
        display: flex;
        align-items: center;
        padding: 4px 0;

        .status-icon {
          color: #909399;
          margin-right: 8px;
          font-size: 16px;
        }

        .status-label {
          font-weight: 600;
          color: #606266;
          margin-right: 8px;
          min-width: 160px;
        }

        .status-value {
          color: #303133;
        }
      }
    }
  }
}
</style>
